import { useMutation } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import * as mime from 'react-native-mime-types';

import { useIntl } from '~/contexts/intl';
import { AppError } from '~/error';
import { ChatMessageAddDocument } from '~/types';
import { ReactNativeFile } from '~/utils';

import { Attachment } from './attachment';

export type NewChatMessage = {
    text: string;
    attachments?: Attachment[];
};

export type ChatClosedError = {
    isChatClosed: boolean;
    message?: string;
};

const fileFromAttachment = (attachment: Attachment): ReactNativeFile => {
    const type = mime.lookup(attachment.name) || undefined;
    return new ReactNativeFile({ uri: attachment.uri, name: attachment.name, type });
};

const isChatClosedError = (error: Error): boolean => {
    return (
        error.message.includes('Chat is closed') ||
        (error.message.includes('PermissionDenied') && error.message.includes('Chat is closed'))
    );
};

export const useAddChatMessage = (chatId: ID) => {
    const [sendMessageMutation] = useMutation(ChatMessageAddDocument);
    const errorHandler = useErrorHandler();
    const { formatMessage } = useIntl();
    const [chatClosedError, setChatClosedError] = useState<ChatClosedError>({ isChatClosed: false });

    const sendMessage = useCallback(
        async (newMessage: NewChatMessage) => {
            const { text, attachments = [] } = newMessage;
            // Clear any previous chat closed error
            setChatClosedError({ isChatClosed: false });

            try {
                await sendMessageMutation({
                    variables: {
                        input: {
                            chatId,
                            message: text,
                            files: attachments.map(attachment =>
                                attachment.file ? attachment.file : fileFromAttachment(attachment)
                            )
                        }
                    }
                });
            } catch (err) {
                if (err instanceof Error && isChatClosedError(err)) {
                    setChatClosedError({
                        isChatClosed: true,
                        message: formatMessage('error.chat-closed')
                    });
                } else if (err instanceof Error) {
                    errorHandler(
                        new AppError(err, 'error.cannot-add-message', {
                            onClose: () => {},
                            name: 'error-overlay.go_back'
                        })
                    );
                } else {
                    errorHandler(err);
                }
            }
        },
        [chatId, errorHandler, sendMessageMutation, formatMessage]
    );

    const clearChatClosedError = useCallback(() => {
        setChatClosedError({ isChatClosed: false });
    }, []);

    return useMemo(
        () => ({
            sendMessage,
            chatClosedError,
            clearChatClosedError
        }),
        [sendMessage, chatClosedError, clearChatClosedError]
    );
};
